import type {
  GtmEventInteractiveAction,
  GtmEventInteractiveCategory,
  GtmEventInteractiveTag,
  GtmPageName,
  GtmPagePath,
  GtmTypeUser,
  IGTMEvent,
  IGTMGamificationEvent,
  IGtmPageVirtualEvent,
  IGTMPurchaseDetailEvent,
  IGTMRefundEvent
} from '@/modules/perseo-billing/interfaces/src/front/interfaces/gtm-group';
import { GtmEvent } from '@/modules/perseo-billing/interfaces/src/front/interfaces/gtm-group';

import type { IPurchaseItems } from '../interfaces/src/shared';

export function getEvent(
  category: string,
  action: string,
  tag: string,
  event?: string,
  value?: string
): IGTMEvent {
  return {
    'event-interactive-accion': action,
    'event-interactive-category': category,
    'event-interactive-tag': tag,
    'event-interactive-value': value,
    event: event || 'event-interactive'
  };
}

export function getGtmInteractionEvent({
  category,
  action,
  tag,
  event,
  value,
  isSavedMethod
}: {
  category: GtmEventInteractiveCategory | string;
  action: GtmEventInteractiveAction | string;
  tag: GtmEventInteractiveTag | string;
  event?: string;
  value?: string;
  isSavedMethod?: boolean;
}): IGTMEvent {
  const withSavedMethod = isSavedMethod === !!isSavedMethod;
  const savedMethod = withSavedMethod
    ? { 'event-interactive-metodoGuardado': isSavedMethod }
    : {};
  return {
    'event-interactive-category': category,
    'event-interactive-accion': action,
    'event-interactive-tag': tag,
    'event-interactive-value': value,
    ...savedMethod,
    event: event || 'event-interactive'
  };
}

export interface ICustomizableEvent {
  category?: GtmEventInteractiveCategory;
  action?: GtmEventInteractiveAction;
  tag?: GtmEventInteractiveTag | string;
  event?: string;
  value?: string;
  isSavedMethod?: boolean;
}
export function getCustomizableEvent({
  category,
  action,
  tag,
  event,
  value
}: ICustomizableEvent): IGTMEvent {
  return {
    ...(category !== undefined
      ? { 'event-interactive-category': category }
      : {}),
    ...(action !== undefined ? { 'event-interactive-accion': action } : {}),
    ...(tag !== undefined ? { 'event-interactive-tag': tag } : {}),
    ...(value !== undefined ? { 'event-interactive-value': value } : {}),
    event: event ?? 'event-interactive'
  };
}

export function getGtmGamificationEvent({
  category,
  action,
  tag,
  level,
  points,
  event
}: {
  category: GtmEventInteractiveCategory;
  action: GtmEventInteractiveAction;
  tag: GtmEventInteractiveTag | string;
  level?: string;
  points?: string;
  event?: string;
}): IGTMGamificationEvent {
  return {
    'event-interactive-category': category,
    'event-interactive-accion': action,
    'event-interactive-tag': tag,
    ...(level && { 'event-interactive-nivel': level }),
    ...(points && { 'event-interactive-puntos': points }),
    event: event || 'event-interactive'
  };
}

interface IGtmPageVirtualEventParams {
  page: GtmPagePath | string;
  pagename: GtmPageName | string;
  typeUser?: GtmTypeUser;
}

export function getGtmPageVirtualEvent({
  page,
  pagename,
  typeUser
}: IGtmPageVirtualEventParams): IGtmPageVirtualEvent {
  return {
    event: GtmEvent.PageVirtual,
    'type-User': typeUser as GtmTypeUser,
    page,
    pagename
  };
}

export function getGtmPaginationEvent({
  category,
  action,
  tag,
  event
}: {
  category: GtmEventInteractiveCategory;
  action: GtmEventInteractiveAction;
  tag: string;
  event?: string;
}): IGTMEvent {
  return {
    'event-interactive-category': category,
    'event-interactive-accion': action,
    'event-interactive-tag': tag,
    event: event || 'event-interactive'
  };
}

export function getCartProductEvent({
  event,
  ...rest
}: IGtmPageVirtualEvent): IGtmPageVirtualEvent {
  return { event, ...rest };
}

export function getPurchaseDetailEvent({
  category,
  action,
  tag,
  orderNumber,
  item,
  event,
  purchaseDate,
  paymentMethod
}: {
  category: string;
  action: string;
  tag: string;
  orderNumber: string;
  item: IPurchaseItems;
  event?: string;
  paymentMethod?: string;
  purchaseDate?: string;
}): IGTMPurchaseDetailEvent {
  const basicEvent = getEvent(category, action, tag, event);
  const { code, trackingInfo = null } = item;
  const { status = '', deliveryDateDesc = '' } = trackingInfo ?? {
    status: '',
    deliveryDateDesc: ''
  };
  return {
    ...basicEvent,
    'event-interactive-ordenCompra': orderNumber,
    'event-interactive-Sku': code,
    'event-interactive-fechaCompra': purchaseDate || '',
    'event-interactive-fechaCompromiso': deliveryDateDesc,
    'event-interactive-estadoCompra': status,
    'event-interactive-metodoPago': paymentMethod || ''
  };
}

export function getRefundEvent({
  category,
  action,
  tag,
  event,
  sku,
  paymentMethod,
  reason,
  quantity,
  date,
  status
}: {
  category: string;
  action: string;
  tag: string;
  event?: string;
  sku: string;
  paymentMethod: string;
  reason?: string;
  quantity?: string;
  date?: string;
  status?: string;
}): IGTMRefundEvent {
  const basicEvent = getEvent(category, action, tag, event);

  const response: IGTMRefundEvent = {
    ...basicEvent,
    'event-interactive-Sku': sku,
    'event-interactive-metodoPago': paymentMethod
  };

  if (reason) response['event-interactive-motivoDevolución'] = reason;
  if (quantity) response['event-interactive-cantidadUnidades'] = quantity;
  if (date) response['event-interactive-fechaDevolución'] = date;
  if (status) response['event-interactive-estadoDevolución'] = status;

  return response;
}
